import { useCallback } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useHandleSubmitWrongPolicyNoCDV } from 'forms/hooks';
import { includes } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import Checkbox from '@gef-ui/form-material/Checkbox';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth/DatePickerFullWidth';
import Agency from 'components/atoms/GefMui/OrganizationStructure/Agency';
import Channel from 'components/atoms/GefMui/OrganizationStructure/Channel';
import Manager from 'components/atoms/GefMui/OrganizationStructure/Manager';
import Ppz from 'components/atoms/GefMui/OrganizationStructure/Ppz';
import Region from 'components/atoms/GefMui/OrganizationStructure/Region';
import Seller from 'components/atoms/GefMui/OrganizationStructure/Seller';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { AUTO_FILL_RULES } from 'constants/general';
import producedProduction from 'features/views/production/common';
import documents from 'features/views/production/documents';
import { FIELDS, PRODUCED_PRODUCTION_FILTER_FORM_NAME } from 'features/views/production/documents/form';
import user from 'features/views/user';
import { getCodelistByName } from 'utils';

const yesNoOptions = [
	{ name: getLabelMessage('common:yes'), code: 1 },
	{ name: getLabelMessage('common:no'), code: 0 },
];

const Filter = () => {
	const dispatch = useDispatch();
	const formData = useFormData();

	const currentRole = useSelector(user.selectors.getCurrentRole);
	const isDisabled = useSelector((state) => producedProduction.selectors.isWithoutPpzDisabledNew(state, formData));
	const ppDocumentTypeCodelist = useSelector(getCodelistByName(codelists.ppDocumentType));

	const disableActionButtons = useCallback(
		// eslint-disable-next-line
		(e) => () => {
			dispatch(producedProduction.actions.disableActionButtons());
		},
		[dispatch],
	);

	const handleReset = useCallback(() => {
		dispatch(producedProduction.actions.resetCriteriasAndTriggerOrgStructureRefetch());
		dispatch(producedProduction.actions.startupProducedProduction());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(producedProduction.actions.checkIfProductionIsValid());
		dispatch(documents.actions.submitDocuments());
	}, [dispatch]);

	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn: handleSubmit });

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={4}>
						<Channel
							fieldName={FIELDS.channel}
							formName={PRODUCED_PRODUCTION_FILTER_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							autoSelect
							onChange={disableActionButtons}
						/>
					</Grid>
					<Grid size={4}>
						<Region
							fieldName={FIELDS.region}
							formName={PRODUCED_PRODUCTION_FILTER_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							autoSelect
							onChange={disableActionButtons}
						/>
					</Grid>
					<Grid size={4}>
						<Agency
							fieldName={FIELDS.agency}
							formName={PRODUCED_PRODUCTION_FILTER_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.REGION_MANAGER)}
							autoSelect
							onChange={disableActionButtons}
						/>
					</Grid>
					<Grid size={4}>
						<Manager
							fieldName={FIELDS.manager}
							formName={PRODUCED_PRODUCTION_FILTER_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.AGENCY_MANAGER)}
							autoSelect
							onChange={disableActionButtons}
						/>
					</Grid>
					<Grid size={4}>
						<Seller
							fieldName={FIELDS.seller}
							formName={PRODUCED_PRODUCTION_FILTER_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.SELLER)}
							autoSelect
							onChange={disableActionButtons}
						/>
					</Grid>
					<Grid size={4}>
						<Ppz
							fieldName={FIELDS.ppz}
							formName={PRODUCED_PRODUCTION_FILTER_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.PPZ)}
							autoSelect={!includes(currentRole, AUTO_FILL_RULES.PPZ)}
							onChange={disableActionButtons}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:policyNo')}>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.policyNumber}
								label={getLabelMessage('producedProduction:policyNo')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.POLICY_NO,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.documentType}
							label={getLabelMessage('producedProduction:documentType')}
							options={ppDocumentTypeCodelist}
							labelPropName="description"
						/>
					</Grid>
					<Grid size={2}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.scan}
							label={getLabelMessage('producedProduction:scan')}
							options={yesNoOptions}
							labelPropName="name"
							valuePropName="code"
						/>
					</Grid>
					<Grid size={2}>
						<DatePickerFullWidth
							fieldName={FIELDS.reportDateFrom}
							label={getLabelMessage('producedProduction:reportDateFrom')}
						/>
					</Grid>
					<Grid size={2}>
						<DatePickerFullWidth
							fieldName={FIELDS.reportDateTo}
							label={getLabelMessage('producedProduction:reportDateTo')}
						/>
					</Grid>
					<Grid size={4}>
						<Checkbox
							defaultValue
							fieldName={FIELDS.withoutPpz}
							label={getLabelMessage('producedProduction:withoutPpz')}
							disabled={!isDisabled}
						/>
					</Grid>
					<Grid size={4} />
					<Grid size={2}>
						<Button variant="outlined" onClick={handleReset} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={2}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							onClickError={handleSubmitWrongPolicyNoCDV}
							fullWidth
							text={getLabelMessage('common:searchButton')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default Filter;
